// frontend/src/features/assignments/assignmentsSlice.js
/**
 * Assignments slice (Phase 5 -> Phase 7 update).
 *
 * Provides a simple, read-only Redux store for assignment-based driver lists.
 * Loads assignment rows for a given site using canonical assignment data,
 * scoped by company and existing UI filters (agency, search).
 *
 * Phase 7 change:
 * - Stop hardcoding MAIN site
 * - Use numeric siteId
 * - Default to sitesSlice.selectedSiteId when siteId not provided
 */
import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { fetchSiteAssignments } from '../../services/assignmentService';

/**
 * Async thunk: load assignments for a site.
 *
 * - Uses selected site context from sitesSlice by default
 * - Reads existing UI filters (agency + search) from Redux
 * - Returns a flat list of assignment rows
 */
export const loadSiteAssignments = createAsyncThunk(
  'assignments/loadSiteAssignments',
  async (
    { siteId, statuses = ['active'] } = {},
    { getState, rejectWithValue }
  ) => {
    const state = getState();

    // Reuse existing UI filters so assignments behave like legacy lists
    const agencyId = state.ui?.filters?.agencyId || '';
    const search = state.ui?.filters?.q || '';

    // ✅ Phase 7: selected site context lives in sitesSlice
    const selectedSiteId = state.sites?.selectedSiteId;

    // ✅ Coerce to number to prevent "3" (string) and other invalid-id edge cases
    const finalSiteIdRaw = siteId ?? selectedSiteId;
    const finalSiteId = Number(finalSiteIdRaw);

    if (!Number.isFinite(finalSiteId) || finalSiteId <= 0) {
      return rejectWithValue('No site selected');
    }

    return await fetchSiteAssignments(finalSiteId, {
      statuses,
      agencyId,
      search,
    });
  }
);

const assignmentsSlice = createSlice({
  name: 'assignments',

  // Keep state intentionally small
  initialState: {
    rows: [],        // assignment rows returned from backend
    loading: false,  // async state for UI
    error: null,     // load error, if any
    lastLoaded: null // timestamp of last successful load
  },

  reducers: {
    /**
     * Replace all assignment rows.
     * Useful for full refreshes or manual overrides.
     */
    setAssignments(state, action) {
      state.rows = Array.isArray(action.payload) ? action.payload : [];
    },

    /**
     * Patch a single assignment row in-place.
     * Intended for real-time updates (e.g. Socket.IO events).
     */
    patchAssignment(state, action) {
      const { assignment_id, patch } = action.payload || {};
      if (!assignment_id) return;

      const idx = state.rows.findIndex(
        (r) => Number(r.assignment_id) === Number(assignment_id)
      );

      if (idx >= 0) {
        state.rows[idx] = {
          ...state.rows[idx],
          ...(patch || {}),
        };
      }
    },
  },

  extraReducers(builder) {
    builder
      // Request started
      .addCase(loadSiteAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      // Request succeeded
      .addCase(loadSiteAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload || [];
        state.lastLoaded = Date.now();
      })

      // Request failed
      .addCase(loadSiteAssignments.rejected, (state, action) => {
        state.loading = false;
        // ✅ supports rejectWithValue('No site selected')
        state.error =
          action.payload || action.error?.message || 'Failed to load assignments';
      });
  },
});

export const { setAssignments, patchAssignment } = assignmentsSlice.actions;
export default assignmentsSlice.reducer;

/* =========================
   Base selectors
   ========================= */

export const selectAssignmentRows = (s) => s.assignments?.rows || [];
export const selectAssignmentsLoading = (s) => !!s.assignments?.loading;
export const selectAssignmentsError = (s) => s.assignments?.error || null;

/**
 * Selector factory for tab-based views.
 *
 * Filters assignments by assignment_status (e.g. 'active')
 * Applies a client-side safety filter for search text
 */
export const makeSelectAssignmentsForTab = (statusLower /* 'active' */) =>
  createSelector(
    [selectAssignmentRows, (state) => state.ui?.filters || {}],
    (rows, { q }) => {
      const query = (q || '').trim().toLowerCase();

      return rows.filter((r) => {
        // ✅ tolerate either assignment_status or status (backend alias drift)
        const rowStatus = r.assignment_status ?? r.status;

        // Match assignment status for the tab
        if (
          String(rowStatus).toLowerCase() !==
          String(statusLower).toLowerCase()
        ) {
          return false;
        }

        // Optional client-side search safety filter
        if (query) {
          // ✅ include vrn fallback (matches other lists)
          const hay = `${r.name || ''} ${r.email || ''} ${
            r.vehicle_registration_number || r.vrn || ''
          }`.toLowerCase();
          if (!hay.includes(query)) return false;
        }

        return true;
      });
    }
  );
